import React, { useState } from "react";
import api from "@/lib/api";
import { Field, Input, Select, Button, Textarea } from "@/components/UI";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";


export function CountModal({ item, onClose, onDone }) {
  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const system = Number(item.quantity || 0);
  const price = Number(item.unit_price || 0);
  const countedNum = counted === "" ? null : Number(counted);
  const diff = countedNum == null ? 0 : Number((countedNum - system).toFixed(4));
  const diffType = countedNum == null ? null : (diff < -1e-9 ? "fire" : diff > 1e-9 ? "fazla" : "tam");
  const lossQty = diffType === "fire" ? -diff : 0;
  const lossCost = Number((lossQty * price).toFixed(2));
  const REASONS = ["Nem kaybı", "Dökülme", "Bozulma / küf", "Fire (doğal)", "Hatalı ölçüm", "Hırsızlık", "Diğer"];

  const submit = async (e) => {
    e.preventDefault();
    if (counted === "") return toast.error("Sayılan miktarı girin");
    if (Number(counted) < 0) return toast.error("Sayılan miktar negatif olamaz");
    setSaving(true);
    try {
      const { data } = await api.post(`/stock/${item.id}/count`, {
        counted_quantity: Number(counted), loss_reason: reason || null, date, notes: notes || null,
      });
      if (data.diff_type === "fire") toast.success(`Fire kaydedildi: ${data.loss_quantity} ${item.unit} · ${data.loss_cost.toLocaleString("tr-TR")} ₺ Zayiat Gideri oluşturuldu`);
      else if (data.diff_type === "fazla") toast.success(`Fazla kaydedildi: +${data.difference} ${item.unit} · stok yukarı düzeltildi`);
      else toast.success("Sayım kaydedildi: sistem ile sayılan eşit (fark yok)");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="count-modal">
      <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="heading text-xl flex items-center gap-2"><MagnifyingGlass size={20} weight="bold" /> {item.name} — Sayım / Mutabakat</h3>
            <p className="text-sm text-[#5e675e]">{item.count_frequency} sayım · Sistemdeki miktar: <b className="text-[#1A1D1A]" data-testid="count-system-qty">{system.toLocaleString("tr-TR")} {item.unit}</b></p>
          </div>
          <button onClick={onClose} className="text-2xl text-[#8a938c] hover:text-[#1A1D1A]" data-testid="count-modal-close">×</button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Sayım Tarihi *"><Input type="date" required value={date} onChange={e => setDate(e.target.value)} data-testid="count-date" /></Field>
          <Field label={`Sayılan (fiziki) miktar (${item.unit}) *`}>
            <Input type="number" step="0.01" min="0" required value={counted} onChange={e => setCounted(e.target.value)} data-testid="count-counted" placeholder={`Depoda fiilen sayılan ${item.unit}`} autoFocus />
          </Field>

          {diffType === "fire" && (
            <Field label="Fire / Zayiat Sebebi">
              <Select value={reason} onChange={e => setReason(e.target.value)} data-testid="count-reason">
                <option value="">Sebep seçin (opsiyonel)...</option>
                {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </Select>
            </Field>
          )}

          <div className="bg-[#F7F6F3] border border-[#E5E2DC] rounded-md p-4 space-y-1.5 text-sm" data-testid="count-recon-box">
            <div className="flex justify-between"><span>Sistemdeki (Hesaplanan):</span><span className="tabnum">{system.toLocaleString("tr-TR")} {item.unit}</span></div>
            <div className="flex justify-between"><span>Sayılan (Fiziki):</span><span className="tabnum">{countedNum == null ? "—" : countedNum.toLocaleString("tr-TR")} {item.unit}</span></div>
            <div className="border-t border-[#E5E2DC] pt-1.5 flex justify-between font-semibold">
              <span>Fark:</span>
              {diffType == null ? <span className="text-[#8a938c]">—</span> :
                diffType === "fire" ? <span className="tabnum text-[#C45B45]" data-testid="count-diff">Fire −{lossQty.toLocaleString("tr-TR")} {item.unit}</span> :
                diffType === "fazla" ? <span className="tabnum text-[#2D6A4F]" data-testid="count-diff">Fazla +{diff.toLocaleString("tr-TR")} {item.unit}</span> :
                <span className="tabnum text-[#2D4A36]" data-testid="count-diff">Fark yok</span>}
            </div>
            {diffType === "fire" && (
              <div className="flex justify-between font-semibold text-base">
                <span>Zayiat Gideri:</span>
                <span className="tabnum text-[#C45B45]" data-testid="count-loss-cost">{lossCost.toLocaleString("tr-TR")} ₺</span>
              </div>
            )}
          </div>

          <Field label="Notlar"><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} data-testid="count-notes" placeholder="Örn: Aylık depo mutabakatı" /></Field>

          <div className="text-xs text-[#5e675e] bg-[#E4EEE6] px-3 py-2 rounded-md">
            ℹ️ Kaydet dediğinizde stok <b>{countedNum == null ? "—" : countedNum} {item.unit}</b>'e eşitlenir.
            {diffType === "fire" && <> Fark <b>Zayiat Gideri</b> olarak Finans'a yazılır.</>}
            {diffType === "fazla" && <> Fazla için finans oluşturulmaz, yalnızca stok yukarı düzeltilir.</>}
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>İptal</Button>
            <Button type="submit" disabled={saving} data-testid="count-submit">{saving ? "Kaydediliyor..." : "Sayımı Kaydet"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
